import type { DashboardAsset } from "../../types.js";
import type {
  DashboardAssetInventoryDto,
  DashboardAssetRecordDto,
} from "../types/assets.api";

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isString(value: unknown): value is string {
  return typeof value === "string";
}

function isValidAssetType(value: unknown): boolean {
  return (
    value === "model" ||
    value === "image" ||
    value === "pdf" ||
    value === "video" ||
    value === "cad"
  );
}

function isValidReadiness(value: unknown): boolean {
  return (
    value === "staged" ||
    value === "processing" ||
    value === "ready" ||
    value === "failed"
  );
}

function isValidMetadata(
  value: unknown,
): value is Array<{ label: string; value: string }> {
  return (
    Array.isArray(value) &&
    value.every(
      (entry) =>
        typeof entry === "object" &&
        entry !== null &&
        isNonEmptyString((entry as { label?: unknown }).label) &&
        isString((entry as { value?: unknown }).value),
    )
  );
}

function assertAssetRecordDto(
  asset: DashboardAssetRecordDto,
): asserts asset is DashboardAssetRecordDto {
  if (
    !isNonEmptyString(asset.id) ||
    !isNonEmptyString(asset.fileName) ||
    !isValidAssetType(asset.assetType) ||
    !isValidReadiness(asset.readiness) ||
    !isNonEmptyString(asset.sizeLabel) ||
    !isNonEmptyString(asset.updatedAtLabel) ||
    !isString(asset.description) ||
    !isValidMetadata(asset.metadata)
  ) {
    throw new Error("Uploaded asset data was invalid.");
  }
}

export function mapAssetRecordToViewModel(
  asset: DashboardAssetRecordDto,
): DashboardAsset {
  assertAssetRecordDto(asset);

  return {
    id: asset.id,
    name: asset.fileName,
    type: asset.assetType,
    readiness: asset.readiness,
    sizeLabel: asset.sizeLabel,
    updatedAtLabel: asset.updatedAtLabel,
    description: asset.description,
    metadata: asset.metadata.map((entry) => ({ ...entry })),
  };
}

export function mapAssetRecordsToViewModels(
  assets: ReadonlyArray<DashboardAssetRecordDto>,
): DashboardAsset[] {
  return assets.map(mapAssetRecordToViewModel);
}

export function mapAssetInventoryToViewModel(
  inventory: DashboardAssetInventoryDto,
): {
  assets: DashboardAsset[];
  countsByType: DashboardAssetInventoryDto["countsByType"];
  countsByReadiness: DashboardAssetInventoryDto["countsByReadiness"];
  storageUsedLabel: string;
  totalAssetsLabel: string;
} {
  return {
    assets: mapAssetRecordsToViewModels(inventory.items),
    countsByType: inventory.countsByType,
    countsByReadiness: inventory.countsByReadiness,
    storageUsedLabel: inventory.storageUsedLabel,
    totalAssetsLabel: inventory.totalAssetsLabel,
  };
}
